import React, {useEffect, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {BellIcon, SaveIcon} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";

type OutletCtx = { eventId: string | null };

const HOURS_OPTIONS = [12, 24, 48, 72] as const;
type HoursBefore = typeof HOURS_OPTIONS[number];

type ReminderForm = {
    enabled: boolean;
    hoursBefore: HoursBefore;
};

export function RemindersStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<ReminderForm>({
        enabled: false,
        hoursBefore: 24,
    });

    useEffect(() => {
        if (!businessId || !eventId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}`);
                setForm({
                    enabled: data?.reminderEnabled ?? false,
                    hoursBefore: (HOURS_OPTIONS as readonly number[]).includes(data?.reminderHoursBefore)
                        ? data.reminderHoursBefore
                        : 24,
                });
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load reminder settings"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    const save = async () => {
        if (!businessId || !eventId) return;
        setSaving(true);
        try {
            await api.patch(`/api/v1/business/${businessId}/events/${eventId}/reminder`, {
                enabled: form.enabled,
                hoursBefore: form.enabled ? form.hoursBefore : null,
            });
            notify.success("Reminder settings saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save reminder settings"));
        } finally {
            setSaving(false);
        }
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/sponsors`);
        else navigate(`/business/events/new/sponsors?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit reminder settings" : "Set up event reminders"}
                description="Send attendees an email reminder before the event starts."
                actions={
                    <Button type="button" variant="primary" size="md" onClick={save} disabled={saving}>
                        {saving ? "Saving…" :
                            <span className="inline-flex items-center gap-2"><SaveIcon className="size-4"/>Save</span>}
                    </Button>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading reminder settings…</div>
            ) : (
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-5 py-4">
                        <BellIcon className="size-4 text-gray-500"/>
                        <h3 className="text-sm font-semibold text-gray-900">Email reminder</h3>
                    </div>

                    <div className="p-5 space-y-6">
                        {/* Enable toggle */}
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="text-sm font-medium text-gray-900">Enable reminder email</div>
                                <div className="mt-1 text-xs text-gray-500">
                                    Attendees who purchased a ticket will receive an email reminder before the event.
                                </div>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={form.enabled}
                                onClick={() => setForm((p) => ({...p, enabled: !p.enabled}))}
                                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                                    form.enabled ? "bg-orange-500" : "bg-gray-200"
                                }`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                                        form.enabled ? "translate-x-5" : "translate-x-0"
                                    }`}
                                />
                            </button>
                        </div>

                        {/* Hours selector */}
                        {form.enabled && (
                            <div>
                                <div className="text-sm font-medium text-gray-900 mb-3">
                                    Send reminder how many hours before the event?
                                </div>
                                <div className="flex gap-3">
                                    {HOURS_OPTIONS.map((h) => (
                                        <button
                                            key={h}
                                            type="button"
                                            onClick={() => setForm((p) => ({...p, hoursBefore: h}))}
                                            className={`rounded-lg border px-5 py-3 text-sm font-medium transition-colors ${
                                                form.hoursBefore === h
                                                    ? "border-orange-600 bg-orange-50 text-orange-700"
                                                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                            }`}
                                        >
                                            {h}h
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    Reminder will be sent {form.hoursBefore} hours before the event starts.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md" onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Sponsors
                </Button>
            </div>
        </div>
    );
}
